import React, { useContext, useEffect, useState } from "react";

import {
  Card,
  CardContent,
  Typography,
  CardActions,
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormLabel,
  FormControl,
  Fade,
  Divider,
} from "@mui/material";

import { CustomButtonPlusIcon } from "./Common/Design/CustomButtonPlusIcon";
import { Cancel, Delete, Edit, Save } from "@mui/icons-material";
import api from "../api/axios";
import { PermissionContext, UserContext } from "./Context/Context";

const ObservationsCards = ({
  view = false,
  date = "", // fecha como string vacía
  title = "Sin título",
  content = "",
  company = {}, // objeto vacío por defecto
  caseType = "",
  created_by = 0,
  created_by_name = "-",
  created_by_surname = "-",
  created_by_object = {
    id: 0,
    full_rol_name: null,
    full_company_name: null,
    rol_relation: {},
    company_relation: {},
  },
  observationId = 0,
  status_name = "Sin estado",
  contact_id = 0,
  setChangeRender = () => {},
  caseTypeRelation = "",
  company_relation = {},
  transferCase = false,
  setImportantCards = () => {},
}) => {
  const { can } = useContext(PermissionContext);
  const { userData } = useContext(UserContext);
  const { user } = userData;
  const [visible, setVisible] = useState(true);

  const Swal = require("sweetalert2");
  const normalizeMainNoteText = (text = "") =>
    String(text)
      .replace(/<br\s*\/?>/gi, "\n")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toUpperCase();

  const preguntasClave = [
    "FECHA NACIMIENTO(EDAD):",
    "ESTADO CIVIL:",
    "HIJOS A SU CARGO:",
    "SITUACION LA",
    "SITUACION LABORAL (FUNCIONES QUE DESEMPENA EN SU CASO):",
    "ALGUNA INVALIDEZ OTORGADA O EN TRAMITE?:",
    "COBRA DEPENDENCIA O ALGUNA OTRA PENSION? (EN CASO AFIRMATIVO INDICAR EL TIPO DE PENSION):",
    "HA PRESENTADO RECIENTEMENTE ALGUNA SOLICITUD?",
    "TIENE RESOLUCION?",
    "EN QUE FECHA LE FUE COMUNICADA?",
    "NO ACEPTADO: AUTORIZO EL USO DE MI CORREO ELECTRONICO PARA EL ENVIO DE INFORMACION COMERCIAL, COMUNICACIONES Y NOTICIAS.",
    "ACEPTADO: HE LEIDO Y ACEPTO EL AVISO LEGAL Y LA POLITICA DE PRIVACIDAD.",
  ];

  const [openEditObservation, setOpenEditObservation] = React.useState(false);
  const [newObservationEdit, setNewObservationEdit] = React.useState("");

  const handleDeleteObservation = async (observationId) => {
    const result = await Swal.fire({
      title: "Eliminar observación",
      text: "Esta acción no se puede deshacer. ¿Deseas continuar?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#1ab394",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    const formData = new FormData();

    try {
      // Mostrar loading mientras se ejecuta la petición
      Swal.fire({
        title: "Eliminando...",
        text: "Por favor espere",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      if (observationId === false) {
        formData.append("contact_id", contact_id);
        formData.append("notas", "");
      } else {
        formData.append("id", observationId);
      }

      await api.post(
        `${process.env.REACT_APP_API_URL}/contacts/delete_note_historical`,
        formData,
      );

      Swal.fire({
        title: "Nota eliminada",
        text: "La observación fue eliminada correctamente.",
        icon: "success",
        confirmButtonColor: "#1ab394",
      });

      // Primero animación, después del fade refresca el padre
      setVisible(false);
      setTimeout(() => {
        setChangeRender((prev) => !prev);
      }, 800);
    } catch (error) {
      console.error(error);

      Swal.fire({
        title: "Error",
        text: "No se pudo eliminar la observación. Intenta nuevamente.",
        icon: "error",
        confirmButtonColor: "#d33",
      });
    }
  };

  const handleEditObservations = async (id) => {
    const result = await Swal.fire({
      title: "¿Deseas actualizar la nota?",
      text: "Se guardarán los cambios realizados.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#1ab394",
      cancelButtonColor: "#d33",
      reverseButtons: true,
      confirmButtonText: "Continuar",
      cancelButtonText: "Cancelar",
      customClass: { popup: "swal-front" },
    });

    if (!result.isConfirmed) return;

    const formData = new FormData();
    formData.append("id", id);
    formData.append("notas", newObservationEdit);

    try {
      Swal.fire({
        title: "Guardando...",
        text: "Por favor espera un momento",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      await api.post(
        `${process.env.REACT_APP_API_URL}/contacts/update_note_historical`,
        formData,
      );

      Swal.fire({
        icon: "success",
        title: "¡Observación actualizada!",
        text: "Los cambios se guardaron correctamente.",
        confirmButtonColor: "#1ab394",
      });

      handleCloseEditObservation();
      setChangeRender((prev) => !prev);
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Error al guardar",
        text:
          error.response?.data?.message || "Algo salió mal. Intenta de nuevo.",
        confirmButtonColor: "#d33",
      });
    }
  };

  const handleClickOpenEditObservation = (id) => {
    setNewObservationEdit(content); // inicializa el textarea con el texto actual
    setOpenEditObservation(true);
  };

  const handleCloseEditObservation = () => {
    setOpenEditObservation(false);
    setNewObservationEdit("");
  };

  const contienePreguntasClave = (texto = "") => {
    const normalizedText = normalizeMainNoteText(texto);
    return preguntasClave.some((pregunta) => normalizedText.includes(pregunta));
  };

  const normalizeTextForTextarea = (htmlText = "") =>
    htmlText.replace(/<br\s*\/?>/gi, "\n");

  // Nota principal: tiene preguntas clave O título reconocido
  const normalizedTitle = normalizeMainNoteText(title);
  const isMainNote =
    contienePreguntasClave(content) ||
    ["PRIMERA NOTA", "REGISTRO INICIAL", "REGISTRO DE CONTACTO"].some(
      (mainTitle) => normalizedTitle.includes(mainTitle),
    );

  const canDeleteNotes =
    // requisito base
    (can("contacts", "deletenotes") || can("clients", "deletenotes")) &&
    // all → puede borrar todas
    (can("contacts", "notes_all") ||
      can("clients", "notes_all") ||
      // own → solo las propias
      ((can("contacts", "notes_own") || can("clients", "notes_own")) &&
        user?.id == created_by_object?.id));

  useEffect(() => {
    if (isMainNote) {
      setImportantCards([
        {
          contact_id: 0,
          created_at: date,
          title: title,
          company: company?.id ?? company,
          caseType: caseTypeRelation,
          created_by: created_by,
          created_by_name: created_by_name,
          created_by_surname: created_by_surname,
          created_by_object: created_by_object,
          observationId: observationId,
          status_name: status_name,
          content: content,
        },
      ]);
    }
  }, [transferCase]);

  // console.log(can("contacts", "deletenotes") || can("clients", "deletenotes"));

  // console.log(company);
  const statusChipColor = {
    "Pte Llamada": { bg: "#f8ac59", color: "white" },
    Cancelado: { bg: "#ed5565", color: "white" },
    Llamado: { bg: "#23c6c8", color: "white" },
    Cliente: { bg: "#1ab394", color: "white" },
  };
  const statusChipStyle = statusChipColor[status_name] || {
    bg: "#e0e0e0",
    color: "#555",
  };

  const canSeeNoteCard =
    isMainNote ||
    company_relation?.id == user?.company_id ||
    user?.rol_relation?.id === 1;

  return (
    canSeeNoteCard && (
      <Fade in={visible} timeout={800}>
        <Card
          sx={{
            mx: "4px",
            my: "8px",
            borderRadius: "12px",
            border: isMainNote
              ? "1px solid rgba(26,179,148,0.4)"
              : "1px solid rgba(0,0,0,0.09)",
            borderLeft: isMainNote ? "4px solid #1ab394" : "4px solid #90caf9",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            transition: "box-shadow 0.2s",
            "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.11)" },
          }}>
          {/* ── Header: tipo de caso + fecha ── */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              py: 0.75,
              backgroundColor: isMainNote
                ? "rgba(26,179,148,0.07)"
                : "rgba(144,202,249,0.1)",
              borderBottom: "1px solid rgba(0,0,0,0.06)",
            }}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: "#555" }}>
              {caseType || "Sin tipo de caso"}
            </Typography>
            <Typography variant="caption" sx={{ color: "#888" }}>
              {date}
            </Typography>
          </Box>

          <CardContent sx={{ pb: 1, pt: 1.5 }}>
            {/* Status + Empresa */}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                mb: 1.5,
                flexWrap: "wrap",
                alignItems: "center",
              }}>
              <Typography variant="caption" sx={{ color: "#888" }}>
                Estado de contacto:
              </Typography>
              <Chip
                size="small"
                label={status_name || "Sin registrar"}
                sx={{
                  fontWeight: 600,
                  fontSize: "11px",
                  height: "22px",
                  backgroundColor: statusChipStyle.bg,
                  color: statusChipStyle.color,
                  borderRadius: "6px",
                }}
              />
              {company &&
                (typeof company === "string"
                  ? company.trim()
                  : company?.company_name) && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={
                      typeof company === "string"
                        ? company
                        : company?.company_name
                    }
                    sx={{
                      fontSize: "11px",
                      height: "22px",
                      borderRadius: "6px",
                    }}
                  />
                )}
              {isMainNote && (
                <Chip
                  size="small"
                  label="Nota principal"
                  color="success"
                  variant="outlined"
                  sx={{ fontSize: "10px", height: "20px", borderRadius: "6px" }}
                />
              )}
            </Box>

            {/* Título */}
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                mb: 0.75,
                fontSize: "13px",
                color: isMainNote ? "#1ab394" : "#333",
              }}>
              {title?.toUpperCase()}
            </Typography>

            {/* Contenido */}
            <Typography
              variant="body2"
              sx={{
                fontSize: "13px",
                color: "#444",
                lineHeight: 1.65,
                textAlign: "left",
              }}>
              <span
                dangerouslySetInnerHTML={{
                  __html: content?.replace(/\n/g, "<br />"),
                }}
              />
            </Typography>
          </CardContent>

          <Divider sx={{ mx: 2, opacity: 0.4 }} />

          <CardActions
            sx={{
              display: "flex",
              justifyContent: "space-between",
              px: 2,
              py: 0.75,
            }}>
            <Typography sx={{ fontSize: "13px", color: "#777" }}>
              Creado por: {created_by_name} {created_by_surname}
            </Typography>

            {contact_id && !view && (
              <Box sx={{ display: "flex", gap: 1 }}>
                {/* Nota principal → solo Editar */}
                {isMainNote && (
                  <Button
                    size="small"
                    color="info"
                    variant="outlined"
                    startIcon={<Edit sx={{ fontSize: "14px !important" }} />}
                    sx={{ fontSize: "13px", textTransform: "none" }}
                    onClick={handleClickOpenEditObservation}>
                    Editar
                  </Button>
                )}

                {/* Notas secundarias → solo Eliminar (para quien la creó) */}
                {!isMainNote && canDeleteNotes && (
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    startIcon={<Delete sx={{ fontSize: "14px !important" }} />}
                    sx={{ fontSize: "13px", textTransform: "none" }}
                    disabled={transferCase}
                    onClick={() =>
                      handleDeleteObservation(
                        observationId ? observationId : false,
                      )
                    }>
                    Eliminar
                  </Button>
                )}
              </Box>
            )}
          </CardActions>

          {/* Diálogo de edición — solo para nota principal */}
          {isMainNote && (
            <Dialog
              open={openEditObservation}
              onClose={handleCloseEditObservation}
              maxWidth="md"
              fullWidth
              sx={{
                "& .MuiPaper-root": { minHeight: "300px", minWidth: "400px" },
              }}>
              <DialogTitle>Editar observación</DialogTitle>
              <DialogContent style={{ paddingTop: "20px" }}>
                <FormControl style={{ width: "100%" }}>
                  <FormLabel sx={{ textAlign: "left", mb: 1 }}>
                    Observación
                  </FormLabel>
                  <textarea
                    style={{
                      height: "400px",
                      resize: "none",
                      padding: "10px 15px",
                      fontSize: "15px",
                      borderRadius: "10px",
                      border: "1px solid rgba(128, 128, 128, 0.35)",
                      color: "rgb(70 69 69)",
                    }}
                    onChange={(e) => setNewObservationEdit(e.target.value)}
                    value={normalizeTextForTextarea(newObservationEdit)}
                  />
                </FormControl>
              </DialogContent>
              <DialogActions>
                <CustomButtonPlusIcon
                  icon={Cancel}
                  text="Cancelar"
                  size="small"
                  position="start"
                  color="error"
                  variant="outlined"
                  handleFunction={handleCloseEditObservation}
                />
                <CustomButtonPlusIcon
                  icon={Save}
                  text="Guardar"
                  size="small"
                  position="start"
                  color="success"
                  variant="outlined"
                  handleFunction={() => handleEditObservations(observationId)}
                />
              </DialogActions>
            </Dialog>
          )}
        </Card>
      </Fade>
    )
  );
};

export default ObservationsCards;
